import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/constants";
import { assetUrl } from "@/lib/api";

const HERO_IMG = assetUrl("/api/static/redesign/hero.webp");

const REASSURANCES = [
  "Professionnels sélectionnés",
  "Accompagnement à chaque étape",
  "Suivi simplifié de votre projet",
];

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
};

export const Hero = () => {
  const navigate = useNavigate();
  return (
  <section data-testid="hero" className="bg-ivory pb-20 pt-28 sm:pt-32 lg:pb-28">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
      <div>
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-xs font-bold uppercase tracking-[0.28em] text-cognac sm:text-sm"
        >
          Rénovation • Aménagement • Construction
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.22 }}
          className="mt-6 font-display text-5xl font-medium leading-[1.05] tracking-tight text-navy sm:text-6xl lg:text-7xl"
        >
          Des espaces de vie où il fait vraiment{" "}
          <span className="italic text-sage">bon vivre.</span>
        </motion.h1>
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.34 }}
          className="mt-6 max-w-lg text-base leading-relaxed text-stone-500 sm:text-lg"
        >
          CozyHome vous accompagne de l'idée à la réception de vos travaux,
          avec des professionnels de confiance et un suivi simplifié.
        </motion.p>
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.46 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-estimate"
            onClick={() => navigate("/estimer")}
            className="group flex items-center gap-2.5 rounded-full bg-navy px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
          >
            Parler de mon projet
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-discover"
            onClick={() => scrollToId("process")}
            className="rounded-full border border-navy/25 px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
          >
            Découvrir CozyHome
          </button>
        </motion.div>
        <motion.ul
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.58 }}
          className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
        >
          {REASSURANCES.map((r) => (
            <li key={r} data-testid={`hero-reassurance-${REASSURANCES.indexOf(r)}`} className="flex items-center gap-2 text-sm font-semibold text-navy/75">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sage/15 text-sage">
                <Check className="h-3 w-3" />
              </span>
              {r}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <img
          data-testid="hero-image"
          src={HERO_IMG}
          alt="Intérieur lumineux d'une maison rénovée avec bois naturel et touches de vert"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          width="1408"
          height="768"
          className="h-[420px] w-full rounded-3xl object-cover shadow-xl shadow-navy/10 sm:h-[520px] lg:h-[580px]"
        />
        <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-lg shadow-navy/10 sm:block">
          <p className="font-display text-xl font-medium text-navy">Un seul accompagnement</p>
          <p className="mt-0.5 text-xs font-semibold text-stone-400">pour simplifier tout votre projet</p>
        </div>
      </motion.div>
    </div>
  </section>
  );
};
